import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  ZoneDocCoverageFormComponent,
  ZoneDocCoverage,
} from '../../zone-doc-coverage-form/zone-doc-coverage-form.component';

// Payload emitted on Save: the single selected file plus the coverage the shared
// zone-doc-coverage-form describes (service key + sub-type keys + raw expiration
// string). The host converts + routes it to its own upload client (vendor →
// document.service, internal → admin.service), so this dialog stays data-access
// agnostic.
export interface ZoneDocUploadResult {
  file: File;
  coverage: ZoneDocCoverage | null;
}

// Shared single-file zone-document upload dialog: a drag-drop/browse drop zone, an
// inline PDF preview (sanitized blob-URL iframe; non-PDFs degrade to a file-type
// chip), and the shared coverage form, with a footer showing the zone + Cancel /
// Save. Extracted verbatim from the vendor Documents tab so the internal console
// reuses the exact same upload UX. Owns only file/preview/coverage UI state — it
// never touches a document client.
@Component({
  standalone: true,
  selector: 'zone-doc-upload-dialog',
  imports: [CommonModule, ZoneDocCoverageFormComponent],
  templateUrl: './zone-doc-upload-dialog.component.html',
  styleUrl: './zone-doc-upload-dialog.component.scss',
})
export class ZoneDocUploadDialogComponent {
  // The host controls presence via *ngIf (so each open mounts a fresh instance
  // with cleared file/preview state). Human-readable zone label for the footer
  // (host decodes the state enum).
  @Input() zoneLabel = '';
  // The pre-scoped service (read-only chip in the coverage form).
  @Input() service: any = null;
  // The scoped sub-service node, or null for a service-level node.
  @Input() scopedSubType: any = null;
  // Sibling document-requiring sub-services offered as "also apply to" checkboxes.
  // For a license upload these are the requirement's configured sub-services under
  // the triggering service (default checked); for a legacy upload they are all
  // enabled sub-services under it.
  @Input() siblings: any[] = [];
  // The named license requirement this upload satisfies. Drives the coverage
  // form's conditional Issue/Expiration date fields (via its date flags) and tags
  // the uploaded document with its id. Null for a legacy (non-license) upload.
  @Input() license: any = null;
  // Disables Save while the host's upload call is in flight.
  @Input() submitting = false;
  // Whether the license requirement has a service-level node in the triggering
  // service (an association with a null sub-type). When true, a service-only
  // coverage (no subs checked) is valid. When false the requirement is sub-only —
  // there is no service-level row for a service-only coverage to satisfy — so at
  // least one configured sub must be checked or the upload would be orphaned.
  // Defaults true (service-level coverage valid); the host always sets it for a
  // license upload. Ignored for a legacy (license == null) upload.
  @Input() hasServiceLevelNode = true;

  get expirationRequired(): boolean {
    return !!this.license?.expirationDateRequired;
  }

  get issueRequired(): boolean {
    return !!this.license?.issueDateRequired;
  }

  @Output() save = new EventEmitter<ZoneDocUploadResult>();
  @Output() cancelled = new EventEmitter<void>();

  // Human-readable service context for the footer: the scoped service, plus the
  // sub-service when the upload is scoped to one (matching the requirement-row
  // label). Empty when no service is scoped.
  get serviceLabel(): string {
    const base = this.service?.displayName ?? '';
    const sub = this.scopedSubType?.displayName;
    return sub ? `${base} — ${sub}` : base;
  }

  // The single selected file + its inline preview. PDFs render in an iframe on a
  // freshly-created object URL; non-PDFs degrade to a file-type chip.
  file: File | null = null;
  isPdf = false;
  previewUrl: SafeResourceUrl | null = null;
  private previewObjectUrl: string | null = null;
  // Latest coverage emitted by the shared coverage form child.
  private coverage: ZoneDocCoverage | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  // Drag-drop + click-to-browse handlers for the single-file drop zone.
  onFileSelect(event: any): void {
    const files = event?.target?.files;
    if (files && files.length > 0) {
      this.setFile(files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event?.dataTransfer?.files;
    if (files && files.length > 0) {
      this.setFile(files[0]);
    }
  }

  // The shared coverage form emits on init and on every change; keep only the
  // latest so Save can read the current coverage without reaching in.
  onCoverageChange(coverage: ZoneDocCoverage): void {
    this.coverage = coverage;
  }

  onCancel(): void {
    this.clearFile();
    this.coverage = null;
    this.cancelled.emit();
  }

  // Whether the license's required date fields are satisfied by the latest
  // coverage. Missing required dates block Save (mirrored by the disabled button).
  get requiredDatesSatisfied(): boolean {
    if (this.expirationRequired && !this.coverage?.expirationDate) {
      return false;
    }
    if (this.issueRequired && !this.coverage?.issueDate) {
      return false;
    }
    return true;
  }

  // Whether the emitted coverage satisfies at least one of the requirement's
  // rows. Legacy (license == null) uploads have no row to satisfy, so no gate. A
  // license upload with a service-level node covers the service-level row with a
  // service-only coverage, so no gate either. But a sub-only requirement (no
  // service-level node) has no service-level row — a service-only coverage would
  // satisfy nothing and orphan the document — so at least one sub must be checked.
  get coverageSelectionSatisfied(): boolean {
    if (!this.license || this.hasServiceLevelNode) {
      return true;
    }
    return (this.coverage?.serviceSubTypes?.length ?? 0) > 0;
  }

  // Whether Save is enabled: a file is selected, no submit is in flight, any
  // license-required dates are present, and the coverage satisfies >= 1 row of a
  // sub-only requirement. A license upload whose requirement has a service-level
  // node (or a legacy upload) needs no sub selected.
  get canSave(): boolean {
    return (
      !!this.file &&
      !this.submitting &&
      this.requiredDatesSatisfied &&
      this.coverageSelectionSatisfied
    );
  }

  // Emits the file + coverage. The Save button is disabled without a file (and
  // without any required dates), so this only fires with a selected file; the
  // host performs the upload.
  onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.save.emit({ file: this.file!, coverage: this.coverage });
  }

  // Records the selected file and builds its preview. A PDF renders inline in an
  // iframe on a fresh object URL (sanitized as a resource URL); any other type
  // has no safe inline render, so the preview falls back to a file-type chip.
  private setFile(file: File): void {
    this.clearFile();
    this.file = file;
    this.isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');
    if (this.isPdf) {
      this.previewObjectUrl = URL.createObjectURL(file);
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.previewObjectUrl,
      );
    }
  }

  // Drops the selected file and revokes any object URL so the blob is released.
  private clearFile(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
    this.file = null;
    this.isPdf = false;
    this.previewUrl = null;
  }
}
